import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Heart, Play, Flame, Sparkles, Gamepad2, Lock, WifiOff, Smartphone, ShieldCheck } from 'lucide-react';
import { InstallButton } from '../InstallPrompt';
import { ProfileButton } from './AuthDialog';
import { useBrand, BrandMark } from '../BrandProvider';
import { BADGE_LABELS, BADGE_COLORS } from '../admin/StoreCMS';
import { Construction } from 'lucide-react';
import { storeApi, coverUrl } from '../../lib/api';
import { db } from '../../lib/db';
import { FOOTER_CONTACTS } from '../../content/legal';
import { RichLine as Rich } from '../LegalPage';

const SORTS = [{ id: 'popular', label: 'Popolari', icon: Flame }, { id: 'new', label: 'Nuovi', icon: Sparkles }, { id: 'plays', label: 'Più giocati', icon: Gamepad2 }];

function Cover({ g, className = '' }) {
  const src = coverUrl(g);
  return src ? <img src={src} alt={g.title} loading="lazy" className={`h-full w-full object-cover ${className}`} />
    : <div className={`h-full w-full bg-gradient-to-br from-violet-900 via-titan-surface to-cyan-900 flex items-center justify-center text-4xl font-extrabold text-white/80 ${className}`}>{g.title[0]}</div>;
}

function StoreCard({ g, i, wide = false }) {
  const nav = useNavigate();
  return (
    <div data-testid={`store-card-${g.id}`} className={`group glass glass-edge rounded-2xl overflow-hidden rise transition-transform duration-300 hover:-translate-y-1 ${wide ? 'w-[260px] sm:w-[300px] shrink-0 snap-start' : ''}`} style={{ animationDelay: `${i * 0.05}s` }}>
      <Link to={`/gioco/${g.id}`} className="relative block aspect-video overflow-hidden">
        <Cover g={g} className="transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] tracking-widest text-cyan-200 uppercase">{g.genre}</span>
        {g.badge && BADGE_LABELS[g.badge] && <span data-testid={`store-badge-${g.id}`} className={`absolute top-3 right-3 rounded-full bg-gradient-to-r ${BADGE_COLORS[g.badge]} px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase shadow-lg`}>{BADGE_LABELS[g.badge]}</span>}
      </Link>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div data-testid={`store-card-title-${g.id}`} className="font-bold text-slate-100 truncate">{g.title}</div>
            <div className="text-[11px] text-slate-500 truncate t-chat">{g.subtitle || g.description || 'Gioco HTML5 · touch, tastiera e gamepad'}</div>
          </div>
          <div className="shrink-0 text-right text-[11px]">
            <div className="text-rose-300 flex items-center gap-1 justify-end"><Heart className="h-3 w-3" />{g.likes}</div>
            <div className="text-slate-400 flex items-center gap-1 justify-end mt-0.5"><Gamepad2 className="h-3 w-3" />{g.plays}</div>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid={`store-card-play-${g.id}`} onClick={() => nav(`/gioco/${g.id}?play=1`)} className="flex-1 flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold bg-violet-600 hover:bg-violet-500 transition-colors"><Play className="h-3.5 w-3.5" />Gioca Ora</button>
          <InstallButton card />
        </div>
      </div>
    </div>
  );
}

function Row({ title, games, testid }) {
  if (!games.length) return null;
  return (
    <section data-testid={testid}>
      <h2 className="text-base md:text-lg font-bold text-slate-100 mb-3">{title}</h2>
      <div className="flex gap-4 overflow-x-auto snap-x pb-3 -mx-4 px-4 sm:mx-0 sm:px-0">{games.map((g, i) => <StoreCard key={g.id} g={g} i={i} wide />)}</div>
    </section>
  );
}

function Hero({ list }) {
  const { brand } = useBrand();
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  useEffect(() => { if (list.length < 2) return; const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 6000); return () => clearInterval(t); }, [list.length]);
  const g = list[idx % Math.max(1, list.length)];
  return (
    <div data-testid="store-hero" className="relative rounded-3xl overflow-hidden border border-violet-500/25 rise min-h-[300px] sm:min-h-[380px]">
      {g ? <Cover key={g.id} g={g} className="absolute inset-0 object-top opacity-70 rise" /> : <div className="absolute inset-0 titan-grid" />}
      <div className="absolute inset-0 bg-gradient-to-t from-titan-bg via-titan-bg/80 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-titan-bg via-titan-bg/60 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-10 min-h-[300px] sm:min-h-[380px]">
        <div className="t-label mb-3 text-cyan-300">{g ? 'In evidenza' : `${brand.name} ${brand.heroLabel}`.toUpperCase()}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-2xl">{g ? g.title : brand.heroTitle}</h1>
        <p className="text-sm sm:text-base text-slate-300 mt-3 max-w-xl t-chat">{g ? (g.description || `${g.genre} · gioca subito su telefono o PC, anche col controller.`) : brand.heroText}</p>
        <div className="flex flex-wrap gap-3 mt-6">
          {g && <button data-testid="hero-play-btn" onClick={() => nav(`/gioco/${g.id}?play=1`)} className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 hover:brightness-110 transition-[filter] shadow-[0_0_30px_-6px_rgb(var(--violet-500)/.9)]"><Play className="h-4 w-4" />GIOCA ORA</button>}
          <div className="flex items-center"><InstallButton big /></div>
        </div>
        {list.length > 1 && (
          <div className="flex gap-1.5 mt-6" data-testid="hero-dots">
            {list.map((x, i) => <button key={x.id} data-testid={`hero-dot-${i}`} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === idx ? 'w-8 bg-cyan-400' : 'w-3 bg-white/25'}`} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Gate({ notice }) {
  const { brand } = useBrand();
  return (
    <div data-testid="store-gate" className="titan-root min-h-[100dvh] flex flex-col items-center justify-center gap-5 px-6 text-center">
      <BrandMark className="h-14 w-14" />
      <h1 className="text-3xl sm:text-4xl font-extrabold text-white">{brand.name}</h1>
      <p className="max-w-md text-sm text-slate-300 t-chat flex items-start gap-2"><Construction className="h-5 w-5 text-amber-300 shrink-0" />{notice}</p>
      <Link to="/dev" data-testid="gate-dev-link" className="mt-4 h-9 w-9 rounded-lg flex items-center justify-center text-slate-600 hover:text-violet-200"><Lock className="h-4 w-4" /></Link>
    </div>
  );
}

export function PublicGate({ children }) {
  const { features } = useBrand();
  if (features.maintenanceMode) return <Gate notice={features.maintenanceNotice || 'Manutenzione in corso.'} />;
  if (!features.enablePublicStore) return <Gate notice="Lo Store è momentaneamente chiuso. Torna presto!" />;
  return children;
}

export default function StoreHome() {
  return <PublicGate><StoreInner /></PublicGate>;
}

function StoreInner() {
  const { brand, features, legal } = useBrand();
  const [games, setGames] = useState(null);
  const [cats, setCats] = useState([]);
  const [genre, setGenre] = useState('');
  const [sort, setSort] = useState('popular');
  const [q, setQ] = useState('');
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    storeApi.categories().then(setCats).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const key = `store:${genre}:${sort}:${q}`;
      storeApi.list({ genre, sort, q }).then((r) => { setGames(r); setOffline(false); db.kvSet(key, r); })
        .catch(async () => { setOffline(true); setGames((await db.kvGet(key)) || (await db.kvGet('store:::popular:')) || []); });
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [genre, sort, q]);

  const browsing = !genre && !q;
  const featured = useMemo(() => (browsing && sort === 'popular' && games?.length ? games.slice(0, 5) : []), [games, browsing, sort]);
  const rows = useMemo(() => {
    if (!browsing || !games?.length) return [];
    const byNew = [...games].sort((a, b) => (b.published_at || '').localeCompare(a.published_at || ''));
    const byPlays = [...games].sort((a, b) => b.plays - a.plays);
    return [
      { title: 'Novità', list: byNew.slice(0, 12), id: 'row-new' },
      { title: 'Più giocati', list: byPlays.slice(0, 12), id: 'row-plays' },
      ...cats.map((c) => ({ title: c.genre, list: games.filter((g) => g.genre === c.genre), id: `row-cat-${c.genre.replace(/\W+/g, '-').toLowerCase()}` })),
    ];
  }, [games, cats, browsing]);

  return (
    <div className="titan-root min-h-[100dvh]" data-testid="store-view">
      <header className="sticky top-0 z-30 pt-safe border-b border-violet-500/15 bg-titan-bg/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-14 px-4 sm:px-8 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="store-brand">
            <BrandMark />
            <span className="text-sm font-extrabold tracking-[.28em] neon-text">{brand.name.toUpperCase()}</span>
            <span className="hidden sm:block t-label">// Store</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <InstallButton />
            <ProfileButton />
            <Link to="/dev" data-testid="store-dev-link" title="Area Riservata Sviluppatore" className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-500 hover:text-violet-200 hover:bg-white/5 transition-colors">
              <Lock className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10 space-y-10">
        <Hero list={featured} />
        <section className="space-y-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="store-categories">
              <button data-testid="cat-all" onClick={() => setGenre('')} className={`shrink-0 rounded-full px-4 py-1.5 text-xs border transition-colors ${!genre ? 'bg-violet-600/40 border-violet-400 text-white' : 'border-slate-700 text-slate-400 hover:border-slate-500'}`}>Tutti</button>
              {cats.map((c) => (
                <button key={c.genre} data-testid={`cat-${c.genre.replace(/\W+/g, '-').toLowerCase()}`} onClick={() => setGenre(c.genre)}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-xs border transition-colors ${genre === c.genre ? 'bg-cyan-500/25 border-cyan-400 text-cyan-50' : 'border-slate-700 text-slate-400 hover:border-slate-500'}`}>{c.genre} <span className="opacity-50">{c.count}</span></button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 md:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input data-testid="store-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca giochi" className="w-full rounded-full bg-black/40 border border-white/10 pl-9 pr-3 py-2 text-xs outline-none focus:border-cyan-400/60 transition-colors" />
              </div>
              <div className="flex rounded-full border border-white/10 overflow-hidden">
                {SORTS.map((s) => (
                  <button key={s.id} data-testid={`sort-${s.id}`} onClick={() => setSort(s.id)} title={s.label}
                    className={`flex items-center gap-1 px-3 py-2 text-[10px] font-semibold transition-colors ${sort === s.id ? 'bg-violet-600/50 text-white' : 'text-slate-400 hover:bg-white/5'}`}><s.icon className="h-3.5 w-3.5" /><span className="hidden lg:inline">{s.label}</span></button>
                ))}
              </div>
            </div>
          </div>
          {offline && <div data-testid="store-offline" className="flex items-center gap-2 text-xs text-amber-300"><WifiOff className="h-4 w-4" />Sei offline: mostro i giochi salvati sul dispositivo</div>}
          {games === null ? (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="aspect-video rounded-2xl bg-white/[.03] animate-pulse" />)}</div>
          ) : games.length === 0 ? (
            <div data-testid="store-empty" className="glass rounded-2xl p-10 text-center">
              <div className="text-lg font-bold text-slate-100">Nessun gioco ancora pubblicato</div>
              <p className="text-sm text-slate-400 t-chat mt-2">Gli sviluppatori stanno forgiando nuovi titoli. Torna presto!</p>
            </div>
          ) : browsing && sort === 'popular' && rows.length ? (
            <div className="space-y-8">{rows.map((r) => <Row key={r.id} title={r.title} games={r.list} testid={r.id} />)}</div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{games.map((g, i) => <StoreCard key={g.id} g={g} i={i} />)}</div>
          )}
        </section>
        <footer className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/5 pt-6 pb-10 text-[11px] text-slate-500">
          <span className="flex items-center gap-2"><Smartphone className="h-4 w-4" />{brand.footerText}</span>
          {features.enableLegalFooter && (
            <span className="flex flex-col items-center gap-2">
              <span className="flex flex-wrap justify-center gap-2">
                <Link to="/legal/privacy" data-testid="footer-privacy-link" className="flex items-center gap-1.5 rounded-full border border-cyan-500/40 px-4 py-2 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/10 transition-colors"><ShieldCheck className="h-4 w-4" />Privacy</Link>
                <Link to="/legal/termini" data-testid="footer-terms-link" className="rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors">Termini</Link>
                <Link to="/legal/cookie" data-testid="footer-cookie-link" className="rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors">Cookie</Link>
                <Link to="/legal/note" data-testid="footer-notice-link" className="rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5 transition-colors">Note Legali</Link>
              </span>
              <Rich text={legal.footerContacts || FOOTER_CONTACTS} testid="footer-contacts" />
              {legal.disclaimerText && <span data-testid="footer-disclaimer" className="max-w-md text-center t-chat">{legal.disclaimerText}</span>}
            </span>
          )}
          <span data-testid="footer-copyright">{brand.copyright || `${brand.name} // ${brand.tagline}`}</span>
        </footer>
      </main>
    </div>
  );
}
